import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Worker, Viewer, ScrollMode, DocumentLoadEvent, PluginFunctions, RenderPageProps, SpecialZoomLevel, Destination, PdfJs } from "@react-pdf-viewer/core";
import { searchPlugin, RenderHighlightsProps } from "@react-pdf-viewer/search";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import * as pdfjsLib from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.js?url";
import "./pdfFactHighlight.css";
import { extractPageText, clearTextCache } from "../../services/pdfTextExtractor";
import {
  fuzzyFindSubstring,
  fuzzyMatchToRegex,
  normalizeForMatching,
  OCR_CHAR_CLASSES,
} from "../../utils/fuzzyTextMatch";

// Configure PDF.js worker to use the locally bundled asset so it shares our origin
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

/**
 * Fact highlight interface
 */
interface FactHighlight {
  id: string;
  text: string;
  pageIndex?: number | null;      // Primary page (backward compat)
  pageIndices?: number[] | null;  // All pages where fact appears (for multi-page chunks)
}

/**
 * Component props interface
 */
export interface CitationSearchProgress {
  currentPage: number;
  totalPages: number;
  percentage: number;
}

interface PdfFactViewerProps {
  blob: Blob | string | null;
  factHighlights?: FactHighlight[];
  activeHighlightId?: string | null;
  searchTrigger?: number;  // Monotonic counter to force re-search when activeHighlightId is unchanged
  onReady?: (info: { searchPlugin: unknown; pageCount: number }) => void;
  onCitationSearchStart?: (totalPages: number) => void;
  onCitationSearchProgress?: (progress: CitationSearchProgress) => void;
  onCitationSearchComplete?: (success: boolean) => void;
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const HYPHEN_CHARS = new Set([
  "-",
  "\u2010",
  "\u2011",
  "\u2012",
  "\u2013",
  "\u2014",
  "\u2015",
]);

// Ligature expansion map for PDF text matching
const LIGATURE_MAP: Record<string, string> = {
  '\ufb00': 'ff',
  '\ufb01': 'fi',
  '\ufb02': 'fl',
  '\ufb03': 'ffi',
  '\ufb04': 'ffl',
  '\ufb05': 'st',
  '\ufb06': 'st',
  '\u0152': 'OE',
  '\u0153': 'oe',
  '\u00c6': 'AE',
  '\u00e6': 'ae',
};

/**
 * Expand ligatures in text to their character sequences
 */
const expandLigatures = (text: string): string => {
  let result = text;
  for (const [ligature, expansion] of Object.entries(LIGATURE_MAP)) {
    result = result.split(ligature).join(expansion);
  }
  return result;
};

const tokenizeHighlightText = (text: string): string[] => {
  if (!text) {
    return [];
  }

  const tokenPattern = /[A-Za-z]+|[0-9]+|[^A-Za-z0-9\s]/g;
  const matches = text.match(tokenPattern);

  return matches ? matches : [];
};

const buildRegexPatternForHighlight = (text: string, useOcrClasses: boolean = false): string | null => {
  if (!text || typeof text !== "string") {
    return null;
  }

  // Normalize: expand ligatures and non-breaking spaces
  let sanitized = expandLigatures(text);
  sanitized = sanitized.replace(/\u00a0/g, " ").trim();

  // Apply Unicode NFC normalization if available
  if (sanitized.normalize) {
    sanitized = sanitized.normalize('NFC');
  }

  if (sanitized.length === 0) {
    return null;
  }

  // Limit pattern length to keep regex performant
  const MAX_PATTERN_LENGTH = 320;
  const truncated =
    sanitized.length > MAX_PATTERN_LENGTH
      ? sanitized.slice(0, MAX_PATTERN_LENGTH)
      : sanitized;

  const tokens = tokenizeHighlightText(truncated);

  if (tokens.length === 0) {
    return null;
  }

  const patternParts: string[] = [];

  tokens.forEach((token, index) => {
    if (HYPHEN_CHARS.has(token)) {
      patternParts.push("[-\\u2010-\\u2015]");
    } else if (useOcrClasses) {
      // Build pattern with OCR character classes for confusable characters
      let tokenPattern = "";
      for (const char of token) {
        if (OCR_CHAR_CLASSES[char]) {
          tokenPattern += OCR_CHAR_CLASSES[char];
        } else {
          tokenPattern += escapeRegExp(char);
        }
      }
      patternParts.push(tokenPattern);
    } else {
      patternParts.push(escapeRegExp(token));
    }

    if (index < tokens.length - 1) {
      patternParts.push("\\s*");
    }
  });

  return patternParts.join("");
};

const PdfFactViewer: React.FC<PdfFactViewerProps> = ({
  blob,
  factHighlights = [],
  activeHighlightId = null,
  searchTrigger = 0,
  onReady = () => {},
  onCitationSearchStart,
  onCitationSearchProgress,
  onCitationSearchComplete,
}) => {
  // Ensure factHighlights is always an array to prevent undefined errors
  const safeFactHighlights = Array.isArray(factHighlights)
    ? factHighlights
    : [];

  // Debug logging

  const [pdfSource, setPdfSource] = useState<Uint8Array | string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
  const [showHighlightFailedToast, setShowHighlightFailedToast] = useState(false);
  const searchPluginInstanceRef = useRef<ReturnType<typeof searchPlugin> | null>(null);
  // Reference to loaded PDF document for text extraction
  // Using 'any' here because react-pdf-viewer's PdfDocument type differs from pdfjs-dist's PDFDocumentProxy
  // but both have the getPage() method we need for text extraction
  const pdfDocumentRef = useRef<any>(null);
  const factToPageIndexRef = useRef(new Map<string, number | null>()); // Maps factHighlightId -> page index (0-based)
  const factToPageIndicesRef = useRef(new Map<string, number[] | null>()); // Maps factHighlightId -> all page indices (for multi-page chunks)
  const factToTextRef = useRef(new Map<string, string>()); // Maps factHighlightId -> highlight text
  const viewerRootRef = useRef<HTMLDivElement | null>(null);

  const centerHighlightInViewport = useCallback(() => {
    const container = viewerRootRef.current;

    if (!container) {
      return;
    }

    const currentHighlight = container.querySelector(
      ".rpv-search__highlight--current"
    );

    if (!currentHighlight) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const highlightRect = currentHighlight.getBoundingClientRect();

    // Calculate the highlight's position relative to the container
    const highlightTopRelativeToContainer = highlightRect.top - containerRect.top + container.scrollTop;

    const containerHeight = container.clientHeight;
    const highlightHeight = highlightRect.height;

    // Account for the facts panel overlay at the bottom of the viewport.
    // The PDF viewer's container doesn't include the overlay, but the overlay
    // visually covers the bottom ~25-35% of the screen. To ensure the highlight
    // is visible above the overlay, we target the upper portion of the viewport.
    // Position the highlight at roughly 35% from the top (instead of 50% center)
    // This keeps it well above where the facts panel typically sits.
    const targetVerticalPosition = 0.35; // 35% from top of viewport

    let targetScrollTop = highlightTopRelativeToContainer - (containerHeight * targetVerticalPosition) + (highlightHeight / 2);

    // If this would require scrolling above the document start,
    // position it with padding from the top instead (15% from top of viewport)
    if (targetScrollTop < 0) {
      targetScrollTop = highlightTopRelativeToContainer - (containerHeight * 0.15);
    }

    // Constrain to valid scroll range
    const maxScrollTop = container.scrollHeight - containerHeight;
    const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

    // Use scrollTo for more reliable scrolling
    container.scrollTo({
      top: finalScrollTop,
      behavior: 'auto'
    });
  }, []);

  // Convert the incoming blob/string into a blob URL for the viewer.
  // Using a blob URL avoids the "detached ArrayBuffer" crash that occurs when
  // PDF.js transfers the underlying buffer to a web worker and a re-render
  // (e.g. keyboard open, orientation change) tries to re-read it.
  useEffect(() => {
    let isCancelled = false;
    let objectUrl: string | null = null;

    const loadPdfData = async () => {
      setIsDocumentLoaded(false);
      setPdfSource(null);

      if (!blob) {
        setIsLoadingPdf(false);
        return;
      }

      if (blob instanceof Blob) {
        setIsLoadingPdf(true);
        if (!isCancelled) {
          objectUrl = URL.createObjectURL(blob);
          setPdfSource(objectUrl);
        }
        setIsLoadingPdf(false);
      } else if (typeof blob === "string") {
        setPdfSource(blob);
        setIsLoadingPdf(false);
      } else {
        setIsLoadingPdf(false);
      }
    };

    loadPdfData();

    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [blob]);

  const transformGetDocumentParams = useCallback(
    (options: PdfJs.GetDocumentParams): any => ({
      ...options,
      // Range requests enabled - allows PDF.js to fetch pages on demand
    }),
    []
  );

  /**
   * Custom render function for search highlights
   * This renders all fact highlights simultaneously with custom styling
   * IMPORTANT: This also handles scrolling to center the first highlight
   */
  const renderHighlights = useCallback(
    (renderProps: RenderHighlightsProps) => {
      if (!renderProps) {
        return <></>;
      }

      const { highlightAreas, getCssProperties } = renderProps;

      if (
        !Array.isArray(highlightAreas) ||
        highlightAreas.length === 0 ||
        typeof getCssProperties !== "function"
      ) {
        return <></>;
      }

      return (
        <>
          {highlightAreas.map((area, index) => {
            const cssProperties = getCssProperties(area);
            return (
              <div
                key={index}
                className="rpv-search__highlight pdf-fact-highlight"
                data-index={index}
                data-highlight-id={activeHighlightId || ""}
                style={cssProperties}
              />
            );
          })}
        </>
      );
    },
    [activeHighlightId]
  );


  // Build fact data maps directly from factHighlights (skip regex building - done on demand during search)
  // This is much faster than building regex patterns for all facts upfront
  useEffect(() => {
    const pageMap = factToPageIndexRef.current;
    const pageIndicesMap = factToPageIndicesRef.current;
    const textMap = factToTextRef.current;
    pageMap.clear();
    pageIndicesMap.clear();
    textMap.clear();

    if (!safeFactHighlights || safeFactHighlights.length === 0) {
      return;
    }

    // Just populate the maps - no regex building needed here
    safeFactHighlights.forEach((factHighlight) => {
      if (!factHighlight?.id) return;

      pageMap.set(
        factHighlight.id,
        typeof factHighlight.pageIndex === "number" && Number.isFinite(factHighlight.pageIndex)
          ? factHighlight.pageIndex
          : null
      );
      pageIndicesMap.set(
        factHighlight.id,
        Array.isArray(factHighlight.pageIndices) && factHighlight.pageIndices.length > 0
          ? factHighlight.pageIndices
          : null
      );
      textMap.set(factHighlight.id, factHighlight.text || "");
    });
  }, [safeFactHighlights]);

  // Custom plugin to intercept jumpToDestination and use scrollIntoView instead
  // This ensures highlights are centered in the viewport instead of at the top
  const scrollCenterPlugin = useMemo(() => {
    return () => {
      const install = (pluginFunctions: PluginFunctions) => {
        const originalJumpToDestination = pluginFunctions.jumpToDestination;

        // Replace jumpToDestination with our custom version
        pluginFunctions.jumpToDestination = async (destination: Destination): Promise<void> => {
          // Don't call the original - it would scroll to top
          // Instead, wait a bit for the highlight to render, then scroll it to center
          setTimeout(() => {
            const container = viewerRootRef.current;
            if (!container) {
              return;
            }

            const highlight = container.querySelector('.rpv-search__highlight');
            if (highlight) {
              highlight.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            } else {
              // Fallback: if no highlight exists, this might be a non-search jump, use original
              originalJumpToDestination(destination);
            }
          }, 100);
        };
      };

      return {
        install,
      };
    };
  }, []);

  const scrollCenterPluginInstance = scrollCenterPlugin();

  // Initialize search plugin with custom highlight rendering
  // IMPORTANT: searchPlugin is a React hook, so it must be called at the top level
  // We'll set keywords dynamically per citation using setKeyword() and setTargetPages()
  const searchPluginInstance = searchPlugin({
    renderHighlights,
  });

  // Initialize zoom plugin
  // Note: react-pdf-viewer plugins behave like hooks and must be called every render
  const zoomPluginInstance = zoomPlugin();
  const { ZoomIn, ZoomOut, CurrentScale } = zoomPluginInstance;

  // Store the search plugin instance in a ref for imperative access
  useEffect(() => {
    searchPluginInstanceRef.current = searchPluginInstance;
  }, [searchPluginInstance]);

  // Wire plugins into active viewer plugins
  // IMPORTANT: scrollCenterPlugin MUST come before searchPlugin to intercept jumpToDestination
  const activeViewerPlugins = useMemo(
    () => [scrollCenterPluginInstance, searchPluginInstance, zoomPluginInstance],
    [scrollCenterPluginInstance, searchPluginInstance, zoomPluginInstance]
  );

  // Handle document load event
  const handleDocumentLoad = useCallback(
    (e: DocumentLoadEvent) => {
      setIsDocumentLoaded(true);

      // Store reference to PDF document for text extraction in fuzzy matching
      pdfDocumentRef.current = e.doc;

      // Clear text cache when loading a new document
      clearTextCache();

      // Notify parent that viewer is ready
      onReady({
        searchPlugin: searchPluginInstanceRef.current,
        pageCount: e.doc.numPages,
      });
    },
    [onReady]
  );

  // Handle active highlight changes (scroll and highlight)
  // Uses tiered matching strategy:
  // Tier 1: Standard regex match
  // Tier 2: Enhanced regex with OCR character classes
  // Tier 3: Fuzzy LCS-based matching with PDF text extraction
  useEffect(() => {
    // Hide any previous toast
    setShowHighlightFailedToast(false);

    if (!isDocumentLoaded || !activeHighlightId) {
      return;
    }

    // Get citation data
    const targetPageIndex = factToPageIndexRef.current.get(activeHighlightId);
    const targetPageIndices = factToPageIndicesRef.current.get(activeHighlightId);
    const highlightText = factToTextRef.current.get(activeHighlightId);

    // Log search start
    console.log(`[Citation] Searching for highlight on page ${targetPageIndex ?? 'unknown'}...`);

    // MECHANISM 1: Direct page scroll (immediate)
    if (
      typeof targetPageIndex === "number" &&
      Number.isFinite(targetPageIndex) &&
      viewerRootRef.current
    ) {
      const pageNodes = viewerRootRef.current.querySelectorAll(".rpv-core__inner-page");
      const targetPageNode = pageNodes && pageNodes[targetPageIndex];
      if (targetPageNode) {
        const prevPageNode = targetPageIndex > 0 ? pageNodes[targetPageIndex - 1] : null;
        if (prevPageNode) {
          prevPageNode.scrollIntoView({ behavior: "auto", block: "start" });
          setTimeout(() => {
            targetPageNode.scrollIntoView({ behavior: "auto", block: "start" });
          }, 50);
        } else {
          targetPageNode.scrollIntoView({ behavior: "auto", block: "start" });
        }
      }
    }

    // MECHANISM 2: Tiered text matching and highlighting
    if (!highlightText || highlightText.trim().length === 0) {
      if (onCitationSearchComplete) {
        onCitationSearchComplete(false);
      }
      return;
    }

    if (!searchPluginInstanceRef.current) {
      if (onCitationSearchComplete) {
        onCitationSearchComplete(false);
      }
      return;
    }

    const { highlight, setTargetPages, clearHighlights } = searchPluginInstanceRef.current;

    if (!highlight) {
      if (onCitationSearchComplete) {
        onCitationSearchComplete(false);
      }
      return;
    }

    // Clear previous highlights
    if (clearHighlights) {
      clearHighlights();
    }

    // Set target page filter - prioritize targetPageIndex first
    const pagesToSearch: number[] = [];
    if (typeof targetPageIndex === "number" && Number.isFinite(targetPageIndex)) {
      pagesToSearch.push(targetPageIndex);
    }
    if (Array.isArray(targetPageIndices) && targetPageIndices.length > 0) {
      for (const idx of targetPageIndices) {
        if (idx !== targetPageIndex) {
          pagesToSearch.push(idx);
        }
      }
    }

    if (setTargetPages) {
      if (pagesToSearch.length > 0) {
        const pageSet = new Set(pagesToSearch);
        setTargetPages((args) => pageSet.has(args.pageIndex));
      } else {
        setTargetPages(() => true);
      }
    }

    // Signal that we're starting citation search
    if (onCitationSearchStart) {
      onCitationSearchStart(pagesToSearch.length);
    }

    // Tiered matching strategy
    const attemptTieredHighlight = async () => {
      // TIER 1: Standard regex match
      const pattern1 = buildRegexPatternForHighlight(highlightText, false);
      if (pattern1) {
        try {
          const regex1 = new RegExp(pattern1, "gi");
          await highlight(regex1);
          await new Promise(resolve => setTimeout(resolve, 100));
          const highlights = viewerRootRef.current?.querySelectorAll('.rpv-search__highlight');
          if (highlights && highlights.length > 0) {
            console.log(`[Citation] Found via TIER 1 (regex) on page ${targetPageIndex}`);
            setTimeout(() => centerHighlightInViewport(), 150);
            return true;
          }
        } catch (error) {
          // Continue to next tier
        }
      }

      // TIER 2: Enhanced regex with OCR character classes
      const pattern2 = buildRegexPatternForHighlight(highlightText, true);
      if (pattern2) {
        try {
          const regex2 = new RegExp(pattern2, "gi");
          await highlight(regex2);
          await new Promise(resolve => setTimeout(resolve, 100));
          const highlights = viewerRootRef.current?.querySelectorAll('.rpv-search__highlight');
          if (highlights && highlights.length > 0) {
            console.log(`[Citation] Found via TIER 2 (OCR-tolerant regex) on page ${targetPageIndex}`);
            setTimeout(() => centerHighlightInViewport(), 150);
            return true;
          }
        } catch (error) {
          // Continue to next tier
        }
      }

      // TIER 3: Fuzzy LCS-based matching
      if (pdfDocumentRef.current && pagesToSearch.length > 0) {
        try {
          const totalPages = pagesToSearch.length;
          for (let i = 0; i < pagesToSearch.length; i++) {
            const pageIdx = pagesToSearch[i];

            // Report progress
            if (onCitationSearchProgress) {
              const percentage = Math.round(((i + 1) / totalPages) * 100);
              onCitationSearchProgress({ currentPage: i + 1, totalPages, percentage });
            }

            const pageContent = await extractPageText(pdfDocumentRef.current, pageIdx);

            if (pageContent.text) {
              const fuzzyResult = fuzzyFindSubstring(highlightText, pageContent.text, 0.75);

              if (fuzzyResult && fuzzyResult.found) {
                const fuzzyRegex = fuzzyMatchToRegex(fuzzyResult.matchedText);
                if (fuzzyRegex) {
                  await highlight(fuzzyRegex);
                  await new Promise(resolve => setTimeout(resolve, 100));
                  const highlights = viewerRootRef.current?.querySelectorAll('.rpv-search__highlight');
                  if (highlights && highlights.length > 0) {
                    console.log(`[Citation] Found via TIER 3 (fuzzy) on page ${pageIdx}, similarity: ${fuzzyResult.similarity.toFixed(2)}`);
                    setTimeout(() => centerHighlightInViewport(), 150);
                    return true;
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error("[Citation] TIER 3 error:", error);
        }
      }

      return false;
    };

    // Execute tiered matching
    attemptTieredHighlight().then((success) => {
      if (!success) {
        console.log(`[Citation] Could not find highlight text in document`);
        setShowHighlightFailedToast(true);
        setTimeout(() => setShowHighlightFailedToast(false), 3000);
      }
      if (onCitationSearchComplete) {
        onCitationSearchComplete(success);
      }
    });
  }, [isDocumentLoaded, activeHighlightId, searchTrigger, centerHighlightInViewport, onCitationSearchStart, onCitationSearchProgress, onCitationSearchComplete]);

  if (!pdfSource) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">
          {isLoadingPdf ? "Loading PDF..." : "PDF unavailable"}
        </p>
      </div>
    );
  }

  return (
    <div className="pdf-citation-viewer h-full relative" ref={viewerRootRef}>
      {/* Toast notification for failed highlight */}
      {showHighlightFailedToast && (
        <div className="absolute top-4 right-4 z-50 animate-fade-in">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 shadow-lg">
            <p className="text-sm text-amber-800">
              Couldn't highlight exact text
            </p>
          </div>
        </div>
      )}
      <Worker workerUrl={workerSrc}>
        <Viewer
          fileUrl={pdfSource}
          plugins={activeViewerPlugins}
          onDocumentLoad={handleDocumentLoad}
          renderLoader={() => null}
          scrollMode={ScrollMode.Vertical}
          transformGetDocumentParams={transformGetDocumentParams}
        />
      </Worker>
      {/* Floating zoom controls */}
      {isDocumentLoaded && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 bg-white/95 rounded-lg shadow-md border border-gray-200 px-2 py-1">
          <ZoomOut>
            {(props) => (
              <button
                onClick={props.onClick}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-600"
                title="Zoom out"
                aria-label="Zoom out"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                </svg>
              </button>
            )}
          </ZoomOut>
          <CurrentScale>
            {(props) => (
              <span className="text-xs text-gray-500 min-w-[3rem] text-center select-none">
                {Math.round(props.scale * 100)}%
              </span>
            )}
          </CurrentScale>
          <ZoomIn>
            {(props) => (
              <button
                onClick={props.onClick}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-600"
                title="Zoom in"
                aria-label="Zoom in"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </button>
            )}
          </ZoomIn>
        </div>
      )}
    </div>
  );
};

export default PdfFactViewer;
